// ── Minimal trade panel (DOM overlay). ──
// Reflects the trade lifecycle relayed by the server:
//   tradeRequested (incoming)  → show accept/decline
//   tradeUpdate    (partner's offer changed) → reflect their side
//   tradeCompleted → toast + close
// The local player can set a Gold offer and confirm. Intentionally light: a confirm-first
// stub with a single Gold field (items/monsters can be layered on later). Degrades to nothing
// when offline. Glassmorphic to match the chat overlay.

export interface TradePanelCallbacks {
  onOffer: (gold: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
  onAccept: (fromSessionId: string) => void;
}

export class TradePanel {
  #root: HTMLDivElement;
  #title: HTMLDivElement;
  #theirs: HTMLDivElement;
  #goldInput: HTMLInputElement;
  #status: HTMLDivElement;
  #cb: TradePanelCallbacks;
  #pendingFrom: string | null = null;

  constructor(cb: TradePanelCallbacks) {
    this.#cb = cb;

    this.#root = document.createElement('div');
    this.#root.style.cssText = [
      'position:fixed',
      'right:16px',
      'bottom:16px',
      'width:280px',
      'padding:14px',
      'border-radius:14px',
      'border:1px solid rgba(255,255,255,0.15)',
      'background:rgba(14,16,24,0.78)',
      'backdrop-filter:blur(10px)',
      'box-shadow:0 8px 30px rgba(0,0,0,0.4)',
      'font-family:system-ui,sans-serif',
      'color:#eaeaea',
      'z-index:1100',
      'display:none',
    ].join(';');

    this.#title = document.createElement('div');
    this.#title.style.cssText = 'font-weight:600;font-size:15px;margin-bottom:8px;color:#7fd1ff';
    this.#title.textContent = 'Trade';

    this.#theirs = document.createElement('div');
    this.#theirs.style.cssText = 'font-size:13px;margin-bottom:8px;color:#b9b9c9';
    this.#theirs.textContent = 'Their offer: —';

    const goldRow = document.createElement('div');
    goldRow.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:8px';
    const goldLabel = document.createElement('span');
    goldLabel.textContent = 'Offer Gold:';
    goldLabel.style.cssText = 'font-size:13px';
    this.#goldInput = document.createElement('input');
    this.#goldInput.type = 'number';
    this.#goldInput.min = '0';
    this.#goldInput.value = '0';
    this.#goldInput.style.cssText =
      'flex:1;min-width:0;padding:6px 8px;border-radius:8px;border:1px solid rgba(255,255,255,0.15);background:rgba(20,22,30,0.6);color:#fff;outline:none;font-size:13px';
    this.#goldInput.addEventListener('keydown', (e) => e.stopPropagation());
    this.#goldInput.addEventListener('change', () => this.#cb.onOffer(this.#gold()));
    goldRow.append(goldLabel, this.#goldInput);

    this.#status = document.createElement('div');
    this.#status.style.cssText = 'font-size:12px;font-style:italic;color:#b9b9c9;margin-bottom:10px;min-height:14px';

    const buttons = document.createElement('div');
    buttons.style.cssText = 'display:flex;gap:8px';
    const confirm = this.#button('Confirm', '#1f8a4c');
    confirm.addEventListener('click', () => {
      if (this.#pendingFrom) {
        this.#cb.onAccept(this.#pendingFrom);
        this.#pendingFrom = null;
        this.setStatus('Accepted — set your offer, then Confirm.');
        return;
      }
      this.#cb.onConfirm();
      this.setStatus('Waiting for partner to confirm…');
    });
    const cancel = this.#button('Cancel', '#8a2f2f');
    cancel.addEventListener('click', () => {
      this.#cb.onCancel();
      this.hide();
    });
    buttons.append(confirm, cancel);

    this.#root.append(this.#title, this.#theirs, goldRow, this.#status, buttons);
    document.body.appendChild(this.#root);
  }

  #button(label: string, color: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = `flex:1;padding:8px;border:none;border-radius:8px;background:${color};color:#fff;cursor:pointer;font-size:13px`;
    return b;
  }

  #gold(): number {
    const n = parseInt(this.#goldInput.value, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  get isOpen(): boolean {
    return this.#root.style.display !== 'none';
  }

  /** Open the panel to send an outgoing trade offer to a partner already requested. */
  openOutgoing(partnerName: string): void {
    this.#pendingFrom = null;
    this.#title.textContent = `Trade with ${partnerName}`;
    this.#theirs.textContent = 'Their offer: —';
    this.#goldInput.value = '0';
    this.setStatus('Request sent. Set your offer, then Confirm.');
    this.#root.style.display = 'block';
  }

  /** Open the panel for an incoming request (Confirm acts as Accept first). */
  openIncoming(fromSessionId: string, fromName: string): void {
    this.#pendingFrom = fromSessionId;
    this.#title.textContent = `${fromName} wants to trade`;
    this.#theirs.textContent = 'Their offer: —';
    this.#goldInput.value = '0';
    this.setStatus('Confirm to accept the trade.');
    this.#root.style.display = 'block';
  }

  /** Reflect the partner's current offer. */
  setPartnerOffer(gold: number, itemIds: string[], monsterIds: string[]): void {
    this.#theirs.textContent = `Their offer: ${gold} Gold, ${itemIds.length} item(s), ${monsterIds.length} monster(s)`;
  }

  setStatus(text: string): void {
    this.#status.textContent = text;
  }

  hide(): void {
    this.#pendingFrom = null;
    this.#root.style.display = 'none';
  }

  destroy(): void {
    this.#root.remove();
  }
}
